import { useEffect, useRef, useState } from "react";
import socket from "../../socket/socket";

const DEBOUNCE_MS = 300;
const RECONCILE_MS = 30000;

const useTrackingResource = (fetcher, branchSwitch, emptyValue) => {
    const [data, setData] = useState(emptyValue);

    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);
    const timerRef = useRef(null);

    const emptyRef = useRef(emptyValue);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;
    const branchRef = useRef(branchSwitch);
    branchRef.current = branchSwitch;

    const branchKey = branchSwitch?._id ?? "default";

    const load = async () => {
        const requestId = ++requestIdRef.current;
        try {
            const res = await fetcherRef.current(branchRef.current);
            if (!mountedRef.current) return;
            if (requestId !== requestIdRef.current) return;
            if (res?.data?.code === 1) setData(res.data.data ?? emptyRef.current);
        } catch (err) {
            console.error("Tracking fetch failed:", err);
        }
    };

    const loadRef = useRef(load);
    loadRef.current = load;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimeout(timerRef.current);
        };
    }, []);

    useEffect(() => {
        setData(emptyRef.current);
        loadRef.current();
    }, [branchKey]);

    useEffect(() => {
        const schedule = () => {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => loadRef.current(), DEBOUNCE_MS);
        };
        const onOrders = (payload) => { if (payload) schedule(); };
        const onReconnect = () => loadRef.current();
        const onVisible = () => {
            if (document.visibilityState === "visible") loadRef.current();
        };

        socket.on("merchant:ordersUpdate", onOrders);
        socket.on("connect", onReconnect);
        document.addEventListener("visibilitychange", onVisible);
        const reconcile = setInterval(() => loadRef.current(), RECONCILE_MS);

        return () => {
            socket.off("merchant:ordersUpdate", onOrders);
            socket.off("connect", onReconnect);
            document.removeEventListener("visibilitychange", onVisible);
            clearInterval(reconcile);
        };
    }, []);

    return data;
};

export default useTrackingResource;